import { IExternalLinkForm, IExternalLinkModel } from '@api/models';
import { Drawer } from '@hyperflake/react-ui-library';
import { FormHelperText, Label } from '@library/forms';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikInput } from 'modules/shared/features/formik';
import useUserListData from 'modules/users/hooks/useUserListData';
import { useMemo } from 'react';
import { toast } from 'react-toastify';
import UserSelectView from '../UserSelectView/UserSelectView';
import { Button } from '@components/buttons';
import * as Yup from 'yup';
import useAddExternalLinkForm from 'modules/documents/hooks/useAddExternalLinkForm';

export const AddExternalLinkSchema = Yup.object({
    path: Yup.string().url('Invalid URL format').required('This field is required'),
    name: Yup.string().trim().required('This field is required'),
});

interface AddExternalLinkDrawerProps {
    clientId: string;
    show: boolean;
    onClose: () => void;
    selectedFolderId: string;
    onAdd: (externalLink: IExternalLinkModel) => void;
}

const AddExternalLinkDrawer = (props: AddExternalLinkDrawerProps) => {
    const { show, onClose, selectedFolderId, onAdd, clientId } = props;

    const addDocumentForm = useAddExternalLinkForm({ clientId, onAdd });

    const userListQuery = useUserListData(clientId);

    const users = useMemo(() => {
        if (userListQuery.isLoading) return [];

        return userListQuery.data;
    }, [userListQuery.isLoading, userListQuery.data]);

    const handleSubmit = async (values: IExternalLinkForm) => {
        onClose();

        try {
            await addDocumentForm.submit({
                ...values,
                folderId: selectedFolderId || '',
            });

            toast.success('External link added successfully.');
        } catch (err: any) {
            if (err?.message !== 'canceled') {
                toast.error(err.data?.message || 'Some error occurred, please try again.');
            }
        }
    };

    return (
        <Drawer open={show} onClose={onClose}>
            <div className="flex flex-col h-[calc(100dvh-3rem)]">
                <h4>Add External Link</h4>

                <>
                    <div className="mt-2">
                        <Formik
                            initialValues={addDocumentForm.initialValues}
                            validationSchema={AddExternalLinkSchema}
                            enableReinitialize
                            onSubmit={handleSubmit}
                        >
                            {({ values, setFieldValue }) => (
                                <Form id="addExternalLinkForm">
                                    <div>
                                        <Label htmlFor="path">URL</Label>

                                        <FormikInput name="path" placeholder="Enter link here" />
                                        <FormHelperText variant={'error'}>
                                            <ErrorMessage name="path" />
                                        </FormHelperText>

                                        {/* <ErrorMessage component="div" className="form-error-message" name="path" /> */}
                                    </div>

                                    <div className="mt-2">
                                        <Label htmlFor="name">File Name</Label>

                                        <FormikInput name="name" placeholder="Enter text here" />
                                        <FormHelperText variant={'error'}>
                                            <ErrorMessage name="name" />
                                        </FormHelperText>

                                        {/* <ErrorMessage component="div" className="form-error-message" name="name" /> */}
                                    </div>

                                    {/* {!selectedCategoryId && (
                                            <div className="mt-6">
                                                <Label htmlFor="name">Category</Label>

                                                <FormikSelect name="folderId" options={folderOptions} />

                                                <ErrorMessage
                                                    component="div"
                                                    className="form-error-message"
                                                    name="folderId"
                                                />
                                            </div>
                                        )} */}

                                    <div className="relative mt-2">
                                        <Label htmlFor="users">Users</Label>

                                        <UserSelectView
                                            users={users}
                                            selectedUsers={values.users}
                                            onSelectedUsersChange={(values) => setFieldValue('users', values)}
                                        />
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>

                    <div className="mt-auto">
                        <div className="flex gap-4 mt-2 pb-4">
                            <Button type="button" color="secondary" size="sm" className="" onClick={onClose}>
                                Cancel
                            </Button>

                            <Button type="submit" form="addExternalLinkForm">
                                Upload
                            </Button>
                        </div>
                    </div>
                </>
            </div>
        </Drawer>
    );
};

export default AddExternalLinkDrawer;
